import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useUpdateBewerber, useMarkAsReviewed, useConfirmBewerber, useMarkBewerber, useUnmarkBewerber } from '../../../api/bewerber';
import { useSendBewerberEmail } from '../../../api/email';
import {
  Bewerber,
  BewerberStatus,
  ONBOARDING_STEPS,
  offeneOnboardingSchritte,
} from '../../../types/bewerber';
import { useAuth } from '../../../context/AuthContext';
import { useConfirm } from '../../../context/ConfirmContext';
import { gewonnenRueckfrage } from '../../../utils/gewonnenRueckfrage';
import { useEinladenBewerber, type EinladenModalProps } from './useEinladenBewerber';
import {
  ActionConfig,
  ModalType,
  STATUS_ACTIONS,
  SECONDARY_ACTIONS,
  DANGER_ACTIONS,
  MARK_REVIEWED_ACTION,
  NICHT_GEEIGNET_ACTION,
  MARK_ACTION,
  UNMARK_ACTION,
  HARD_DELETE_ACTION,
} from './statusActions.config.js';

export interface ModalState {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

export interface UseBewerberActionsOptions {
  bewerber: Bewerber;
  onActionComplete?: () => void;
  includeSecondaryActions?: boolean;
  includeDangerActions?: boolean;
}

export interface UseBewerberActionsReturn {
  // All actions grouped
  primaryActions: ActionConfig[];
  secondaryActions: ActionConfig[];
  dangerActions: ActionConfig[];
  allActions: ActionConfig[];

  // Loading state
  isLoading: boolean;
  loadingAction: string | null;

  // Action handler
  handleAction: (actionConfig: ActionConfig) => Promise<void>;

  // Modal states (the einladen confirmation modal is owned by useEinladenBewerber
  // and exposed via `einladenModalProps` below)
  modals: {
    zurKlaerung: ModalState;
    kennenlernen: ModalState;
    arbeitsvertrag: ModalState;
    absage: ModalState;
    reactivate: ModalState;
    nichtGeeignet: ModalState;
    hardDelete: ModalState;
    anfordern: ModalState;
  };

  /** Spread onto an `<EinladenModal />` to render the re-invite confirmation. */
  einladenModalProps: EinladenModalProps;
}

export function useBewerberActions({
  bewerber,
  onActionComplete,
  includeSecondaryActions = true,
  includeDangerActions = true,
}: UseBewerberActionsOptions): UseBewerberActionsReturn {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Named apart from confirmMutation, which confirms the Bewerber (Bestätigen).
  const confirmDialog = useConfirm();
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  // Shared invite flow (email-then-status, blocks status change on email failure).
  // Used by the "Einladung senden" action button and context menu.
  const einladen = useEinladenBewerber();

  // Helper to build template-specific data for non-einladen email actions.
  // The einladen template's data is built inside useEinladenBewerber.
  const getEmailTemplateData = useCallback(() => {
    return {
      senderName: user ? `${user.firstName} ${user.lastName}` : 'Juniva Team',
    };
  }, [user]);

  // Modal states
  const [isZurKlaerungOpen, setIsZurKlaerungOpen] = useState(false);
  const [isKennenlernenOpen, setIsKennenlernenOpen] = useState(false);
  const [isArbeitsvertragOpen, setIsArbeitsvertragOpen] = useState(false);
  const [isAbsageOpen, setIsAbsageOpen] = useState(false);
  const [isReactivateOpen, setIsReactivateOpen] = useState(false);
  const [isNichtGeeignetOpen, setIsNichtGeeignetOpen] = useState(false);
  const [isHardDeleteOpen, setIsHardDeleteOpen] = useState(false);
  const [isAnfordernOpen, setIsAnfordernOpen] = useState(false);

  // Mutations
  const updateMutation = useUpdateBewerber();
  const sendEmailMutation = useSendBewerberEmail();
  const markAsReviewedMutation = useMarkAsReviewed();
  const confirmMutation = useConfirmBewerber();
  const markMutation = useMarkBewerber();
  const unmarkMutation = useUnmarkBewerber();

  // Build actions for current status
  const primaryActions = useMemo(() => {
    return STATUS_ACTIONS[bewerber.status] || [];
  }, [bewerber.status]);

  const secondaryActions = useMemo(() => {
    if (!includeSecondaryActions) return [];

    // Start with navigation actions
    const actions: ActionConfig[] = SECONDARY_ACTIONS.map(action => ({
      ...action,
      navigateTo: action.navigateTo?.replace(':id', bewerber.id),
    }));

    // Add mark/unmark action based on current state
    if (bewerber.isMarked) {
      actions.push(UNMARK_ACTION);
    } else {
      actions.push(MARK_ACTION);
    }

    return actions;
  }, [bewerber.id, bewerber.isMarked, includeSecondaryActions]);

  const dangerActions = useMemo(() => {
    if (!includeDangerActions) return [];

    const actions: ActionConfig[] = [];

    // Add nicht-geeignet for non-VERLOREN, non-GEWONNEN, non-NICHT_GEEIGNET statuses
    // (except for NEU and VORSTELLUNG where it's already a primary action)
    if (
      bewerber.status !== BewerberStatus.VERLOREN &&
      bewerber.status !== BewerberStatus.GEWONNEN &&
      bewerber.status !== BewerberStatus.NICHT_GEEIGNET &&
      bewerber.status !== BewerberStatus.NEU &&
      bewerber.status !== BewerberStatus.VORSTELLUNG
    ) {
      actions.push(NICHT_GEEIGNET_ACTION);
    }

    // Add absage for non-VERLOREN and non-GEWONNEN statuses
    if (bewerber.status !== BewerberStatus.VERLOREN && bewerber.status !== BewerberStatus.GEWONNEN) {
      actions.push(DANGER_ACTIONS[0]); // Absage
    }

    // Add mark-reviewed if has second application
    if (bewerber.hasSecondApplication) {
      actions.push(MARK_REVIEWED_ACTION);
    }

    // Hard delete action - only for NEU status
    if (bewerber.status === BewerberStatus.NEU) {
      actions.push(HARD_DELETE_ACTION);
    }

    return actions;
  }, [bewerber.status, bewerber.hasSecondApplication, includeDangerActions]);

  const allActions = useMemo(() => {
    return [...primaryActions, ...secondaryActions, ...dangerActions];
  }, [primaryActions, secondaryActions, dangerActions]);

  // Modal state objects
  const modals = useMemo(() => ({
    zurKlaerung: {
      isOpen: isZurKlaerungOpen,
      open: () => setIsZurKlaerungOpen(true),
      close: () => setIsZurKlaerungOpen(false),
    },
    kennenlernen: {
      isOpen: isKennenlernenOpen,
      open: () => setIsKennenlernenOpen(true),
      close: () => setIsKennenlernenOpen(false),
    },
    arbeitsvertrag: {
      isOpen: isArbeitsvertragOpen,
      open: () => setIsArbeitsvertragOpen(true),
      close: () => setIsArbeitsvertragOpen(false),
    },
    absage: {
      isOpen: isAbsageOpen,
      open: () => setIsAbsageOpen(true),
      close: () => setIsAbsageOpen(false),
    },
    reactivate: {
      isOpen: isReactivateOpen,
      open: () => setIsReactivateOpen(true),
      close: () => setIsReactivateOpen(false),
    },
    nichtGeeignet: {
      isOpen: isNichtGeeignetOpen,
      open: () => setIsNichtGeeignetOpen(true),
      close: () => setIsNichtGeeignetOpen(false),
    },
    hardDelete: {
      isOpen: isHardDeleteOpen,
      open: () => setIsHardDeleteOpen(true),
      close: () => setIsHardDeleteOpen(false),
    },
    anfordern: {
      isOpen: isAnfordernOpen,
      open: () => setIsAnfordernOpen(true),
      close: () => setIsAnfordernOpen(false),
    },
  }), [isZurKlaerungOpen, isKennenlernenOpen, isArbeitsvertragOpen, isAbsageOpen, isReactivateOpen, isNichtGeeignetOpen, isHardDeleteOpen, isAnfordernOpen]);

  // Main action handler
  const handleAction = useCallback(async (actionConfig: ActionConfig) => {
    // Ignore disabled ("Bald verfügbar") actions
    if (actionConfig.disabled) return;

    // Handle navigation action. The :id placeholder is resolved here rather than only in
    // the secondaryActions memo, so a navigate action configured on any status works too.
    if (actionConfig.action === 'navigate' && actionConfig.navigateTo) {
      navigate(actionConfig.navigateTo.replace(':id', bewerber.id));
      return;
    }

    // Handle modal action
    if (actionConfig.action === 'modal' && actionConfig.modalType) {
      const modalType = actionConfig.modalType as ModalType;
      if (modalType === 'zurKlaerung') setIsZurKlaerungOpen(true);
      else if (modalType === 'kennenlernen') setIsKennenlernenOpen(true);
      else if (modalType === 'arbeitsvertrag') setIsArbeitsvertragOpen(true);
      else if (modalType === 'absage') setIsAbsageOpen(true);
      else if (modalType === 'reactivate') setIsReactivateOpen(true);
      else if (modalType === 'nichtGeeignet') setIsNichtGeeignetOpen(true);
      else if (modalType === 'hardDelete') setIsHardDeleteOpen(true);
      else if (modalType === 'anfordern') setIsAnfordernOpen(true);
      return;
    }

    // Handle API call action
    if (actionConfig.action === 'apiCall') {
      setLoadingAction(actionConfig.key);
      try {
        if (actionConfig.apiAction === 'markReviewed') {
          await markAsReviewedMutation.mutateAsync(bewerber.id);
          toast.success('Bewerber als geprüft markiert');
        } else if (actionConfig.apiAction === 'mark') {
          await markMutation.mutateAsync(bewerber.id);
          toast.success('Bewerber markiert');
        } else if (actionConfig.apiAction === 'unmark') {
          await unmarkMutation.mutateAsync(bewerber.id);
          toast.success('Markierung entfernt');
        }
        onActionComplete?.();
      } catch (error: any) {
        console.error('API action failed:', error);
        const errorMessage = error?.response?.data?.message || 'Aktion fehlgeschlagen';
        toast.error(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
      } finally {
        setLoadingAction(null);
      }
      return;
    }

    // Handle conditional modal action (Einladen) - delegated to useEinladenBewerber.
    // The shared hook handles: send email first, only change status if email succeeds,
    // and shows the EinladenModal confirmation when this is a repeat invitation.
    if (actionConfig.action === 'conditionalModal') {
      einladen.invite(bewerber, onActionComplete);
      return;
    }

    // Offene Einstellen-Schritte sperren GEWONNEN nicht mehr, sie fragen nach —
    // vor dem Ladezustand, damit der Knopf während der Frage nicht schon dreht.
    if (actionConfig.action === 'statusChange' && actionConfig.newStatus === BewerberStatus.GEWONNEN) {
      const rueckfrage = gewonnenRueckfrage(
        offeneOnboardingSchritte(bewerber),
        ONBOARDING_STEPS.length,
        'bewerber',
      );
      if (rueckfrage && !(await confirmDialog(rueckfrage))) return;
    }

    // Handle email-only and statusChange actions
    setLoadingAction(actionConfig.key);

    try {
      // Handle email-only action
      if (actionConfig.action === 'email' && actionConfig.templateName) {
        if (!bewerber.email) {
          toast.warning('Bewerber hat keine E-Mail-Adresse');
          setLoadingAction(null);
          return;
        }

        await sendEmailMutation.mutateAsync({
          bewerberId: bewerber.id,
          data: {
            templateName: actionConfig.templateName,
            data: getEmailTemplateData(),
          },
        });

        // If this is a confirmation email, mark bewerber as confirmed
        if (actionConfig.confirmAfterEmail) {
          await confirmMutation.mutateAsync(bewerber.id);
          toast.success('E-Mail gesendet und Bewerber bestätigt');
        } else {
          toast.success('E-Mail erfolgreich gesendet');
        }
      }

      // Handle status change action
      if (actionConfig.action === 'statusChange' && actionConfig.newStatus) {
        // If there's a template, send email first (if bewerber has email)
        if (actionConfig.templateName && bewerber.email) {
          try {
            await sendEmailMutation.mutateAsync({
              bewerberId: bewerber.id,
              data: {
                templateName: actionConfig.templateName,
                data: getEmailTemplateData(),
              },
            });
          } catch (emailError) {
            console.error('Failed to send email, but continuing with status change:', emailError);
            toast.warning('E-Mail konnte nicht gesendet werden, Status wird trotzdem geändert');
          }
        } else if (actionConfig.templateName && !bewerber.email) {
          toast.info('Bewerber hat keine E-Mail-Adresse - Status wird ohne E-Mail geändert');
        }

        // Update status
        await updateMutation.mutateAsync({
          id: bewerber.id,
          data: { status: actionConfig.newStatus },
        });
        toast.success('Status erfolgreich geändert');
      }

      onActionComplete?.();
    } catch (error: any) {
      console.error('Action failed:', error);
      const errorMessage = error?.response?.data?.message || 'Aktion fehlgeschlagen';
      toast.error(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
    } finally {
      setLoadingAction(null);
    }
  }, [
    navigate,
    confirmDialog,
    bewerber,
    einladen,
    sendEmailMutation,
    updateMutation,
    markAsReviewedMutation,
    confirmMutation,
    markMutation,
    unmarkMutation,
    onActionComplete,
    getEmailTemplateData,
  ]);

  return {
    primaryActions,
    secondaryActions,
    dangerActions,
    allActions,
    isLoading: loadingAction !== null || einladen.isInviting,
    loadingAction,
    handleAction,
    modals,
    einladenModalProps: einladen.modalProps,
  };
}
